/**
 * 控えめな通知（トースト）。
 *
 * docs/01-requirements.md §5.10: 競合解決ダイアログは出さない。
 * 「以前の内容を履歴に退避しました」のような事後報告を、作業を止めずに伝えるためだけに使う。
 * モーダルにしないこと。
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface Toast {
  id: number;
  message: string;
  /** 押せる操作（「開く」「元に戻す」など）。 */
  action?: { label: string; run: () => void };
}

interface ToastApi {
  show: (message: string, action?: Toast['action'], durationMs?: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('ToastProvider の外で useToast が呼ばれました');
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const show = useCallback((message: string, action?: Toast['action'], durationMs = 6000) => {
    const id = nextId.current++;
    setToasts((prev) => [...prev.slice(-2), { id, message, ...(action ? { action } : {}) }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, durationMs);
  }, []);

  const api = useMemo<ToastApi>(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className="toast">
            <span>{toast.message}</span>
            {toast.action && (
              <button
                type="button"
                className="toast-action"
                onClick={() => {
                  toast.action?.run();
                  setToasts((prev) => prev.filter((t) => t.id !== toast.id));
                }}
              >
                {toast.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
